'use client'

import { Clock, Zap, Truck } from 'lucide-react'
import { LeatherHeader, visualFor } from '@/components/rental-visuals'
import workshopDataEn from '@/content/mtb-workshop-services-summer-2025.json'
import workshopDataFr from '@/content/mtb-workshop-services-summer-2025.fr.json'
import workshopDataNl from '@/content/mtb-workshop-services-summer-2025.nl.json'
import workshopDataEs from '@/content/mtb-workshop-services-summer-2025.es.json'
import workshopDataIt from '@/content/mtb-workshop-services-summer-2025.it.json'

type SummerWorkshopServicesTableProps = {
  locale: string
}

export default function SummerWorkshopServicesTable({ locale }: SummerWorkshopServicesTableProps) {
  // Select the correct translation based on locale
  const getTranslatedData = () => {
    switch (locale) {
      case 'fr':
        return workshopDataFr
      case 'nl':
        return workshopDataNl
      case 'es':
        return workshopDataEs
      case 'it':
        return workshopDataIt
      default:
        return workshopDataEn
    }
  }

  const translatedData = getTranslatedData()

  return (
    <div className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div id="workshop-services" className="text-center mb-10 scroll-mt-24">
          <h2 className="text-4xl md:text-6xl font-bold font-[family-name:var(--font-bebas-neue)] mb-4">
            {translatedData.title || `MTB Workshop Services ${translatedData.season}`}
          </h2>
          {translatedData.subtitle && (
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-6">
            {translatedData.subtitle}
          </p>
          )}

          {/* Book Workshop Slot Button */}
          <a
            href="#workshop-services"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white rounded-lg font-bold text-lg hover:opacity-90 transition-all shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {locale === 'fr' ? 'Réserver un créneau atelier VTT' :
             locale === 'es' ? 'Reservar cita taller MTB' :
             locale === 'nl' ? 'Boek MTB werkplaats tijdslot' :
             locale === 'it' ? 'Prenota slot officina MTB' :
             'Book MTB Workshop Slot'}
          </a>
        </div>

        {/* Service Notes - Top */}
        <div className="mb-12 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div className="bg-card border-2 border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start gap-4">
              <div className="bg-[url('/leather.jpg')] bg-cover bg-center p-3 rounded-lg border border-[#2a1f10]/50">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2 text-foreground">
                  {translatedData.notes.expressTitle || 'Express Service'}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {translatedData.notes.express}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card border-2 border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start gap-4">
              <div className="bg-[url('/leather.jpg')] bg-cover bg-center p-3 rounded-lg border border-[#2a1f10]/50">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2 text-foreground">
                  {locale === 'fr' ? 'Service navette - 15€' :
                   locale === 'es' ? 'Servicio de traslado - 15€' :
                   locale === 'nl' ? 'Shuttle service - 15€' :
                   locale === 'it' ? 'Servizio navetta - 15€' :
                   'Shuttle Service - 15€'}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {locale === 'fr' ? 'Récupération et dépôt de votre VTT directement à votre lieu de résidence.' :
                   locale === 'es' ? 'Recogida y entrega de su MTB directamente en su alojamiento.' :
                   locale === 'nl' ? 'Ophalen en afleveren van uw MTB direct bij uw verblijf.' :
                   locale === 'it' ? 'Ritiro e consegna della vostra MTB direttamente presso il vostro alloggio.' :
                   'Pick up and drop off your MTB directly at your accommodation.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Service Categories */}
        <div className="space-y-16">
          {translatedData.categories.map((category) => (
            <div key={category.id} className="bg-card border-2 border-border rounded-2xl shadow-lg overflow-hidden">
              {/* Leather category header with colour-coded circled icon */}
              <LeatherHeader name={category.name} {...visualFor(category.id)} />

              {/* Services Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <tbody>
                    {category.services.map((service, index) => (
                      <tr
                        key={service.id}
                        className={`flex flex-col border-t border-border transition-colors md:table-row md:hover:bg-muted/30 ${
                          index % 2 === 0 ? 'bg-background' : 'bg-muted/10'
                        }`}
                      >
                        {/* Service Name (+ description on mobile) */}
                        <td className="block px-4 pt-4 md:table-cell md:px-8 md:py-6">
                          <div className="font-semibold text-lg text-foreground">
                            {service.name}
                          </div>
                          <div className="md:hidden mt-1 text-sm text-foreground/75">
                            {service.description}
                          </div>
                        </td>

                        {/* Description - Desktop only */}
                        <td className="hidden md:table-cell md:px-8 md:py-6 text-foreground/75 max-w-md">
                          {service.description}
                        </td>

                        {/* Duration (+ price beside it on mobile) */}
                        <td className="flex items-center justify-between gap-4 px-4 pb-4 pt-3 md:table-cell md:px-8 md:py-6 md:text-center">
                          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-sm font-medium">
                            <Clock className="w-4 h-4" />
                            {service.duration}
                          </div>
                          <div className="text-xl font-bold text-primary md:hidden">
                            {typeof service.price === 'number' ? `${service.price}€` : service.price}
                          </div>
                        </td>

                        {/* Price - Desktop only */}
                        <td className="hidden md:table-cell md:px-8 md:py-6 text-right">
                          <div className="text-2xl font-bold text-primary">
                            {typeof service.price === 'number' ? `${service.price}€` : service.price}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
